import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore.ts';

const breadcrumbLabelMap: Record<string, string> = {
  home: 'breadcrumb.home',
  wizard: 'breadcrumb.wizard',
  newSession: 'breadcrumb.newSession',
  contacts: 'breadcrumb.contacts',
  voicemail: 'breadcrumb.voicemail',
  intent: 'breadcrumb.intent',
  dashboard: 'breadcrumb.dashboard',
  overview: 'breadcrumb.overview',
  integrations: 'breadcrumb.integrations',
  calendar: 'breadcrumb.calendar',
  adminconsole: 'breadcrumb.adminConsole',
  settings: 'breadcrumb.settings',
  users: 'breadcrumb.users',
  organizations: 'breadcrumb.organizations',
  numbers: 'breadcrumb.numbers',
  account: 'breadcrumb.account',
  app: 'breadcrumb.app',
  outreach: 'breadcrumb.outreach',
};

const isId = (segment: string): boolean => {
  return (
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      segment,
    ) ||
    /^[0-9a-f]{24}$/i.test(segment) // MongoDB ObjectId
  );
};

// Segments to exclude from breadcrumbs (used as URL structure only)
const excludedSegments = ['session'];

const fallbackLabel = (segment: string): string => {
  return decodeURIComponent(segment)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const DynamicBreadcrumbSetter = (): null => {
  const location = useLocation();
  const setItems = useBreadcrumbStore((state) => state.setItems);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const isOnlyHome =
      segments.length === 1 && segments[0].toLowerCase() === 'home';

    // Filter out excluded segments but keep track of full path for href
    const breadcrumbs = isOnlyHome
      ? []
      : segments
        .filter((segment) => !excludedSegments.includes(segment.toLowerCase()))
        .map((segment) => {
          // Build href using original segments up to current position
          const originalIndex = segments.indexOf(segment);
          const href = '/workspace/' + segments.slice(0, originalIndex + 1).join('/');
          const labelKey = isId(segment)
            ? segment // Use the ID directly instead of generic 'details'
            : breadcrumbLabelMap[segment.toLowerCase()] ??
                fallbackLabel(segment);

          return { labelKey, href };
        });

    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      ...breadcrumbs,
    ]);
  }, [location.pathname, setItems]);

  return null;
};
